import ReactLoader from 'components/Loader';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPhone } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { DropdownMenu, DropdownToggle, Dropdown } from 'reactstrap';
import io from 'socket.io-client';
import { apiUc } from '../../../../http';
import store from '../../../../store';
import {
  actionCreatorLogin,
  actionCreatorLogout
} from '../../../../store/action/telephonyServicePanel/loginAction';
import LoginForm from './LoginForm';
import LogoutForm from './LogoutFormTelephony';
import PauseUnpauseForm from '../shared/components/PauseUnpauseForm';
import ServicePanelItem from '../ServicePanelItem';
import * as S from '../styles';
import { agentStatusColors } from '../utils/colorByState';
import { queueAgentEvents } from '../utils/events';
import { queueAgentStates } from '../utils/states';
import UptimeCounter from '../UptimeCounter';
import PauseService from '../../../../views/Pause/service';
import { ButtonLoginInNewQueue } from '../sharedComponents/ButtonLoginInNewQueue';
import { HeadersModalLoginQueues } from '../shared/components/headerModalLoginQueues';
import { AddQueueForm } from '../shared/components/AddQueueForm/AddQueueForm';
import { useDispatch } from 'react-redux';
import useQueues from 'hooks/useQueues';
import { updateLoggedPhoneQueues } from 'store/action/queuesActions';

export const TelephonyServicePanel = () => {
  const [agentSessions, setAgentSessions] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const { response: queues } = useQueues({ types: ['is_telephony'] });
  const [pauses, setPauses] = useState([]);
  const [allPauses, setAllPauses] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [currentPauseId, setCurrentPauseId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [addAnotherQueue, setAddAnotherQueue] = useState(false);
  const [activePauseCallData, setActivePauseCallData] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dispatch = useDispatch();

  const socket = useMemo(
    () =>
      io(process.env.REACT_APP_PROXY_URL, {
        path: process.env.REACT_APP_AMI_SOCKET_PATH,
        transports: ['websocket'],
        upgrade: false,
        query: { token: localStorage.getItem('token') }
      }),
    []
  );
  const socketWarning = useMemo(
    () =>
      io(process.env.REACT_APP_PROXY_URL, {
        path: process.env.REACT_APP_SCALE_PATH_SOCKET
      }),
    []
  );

  const isLogged = !!agentSessions && !!agentSessions.length;

  useEffect(() => {
    if (agentSessions.length > 0) {
      localStorage.setItem(
        'currentLoggedInExtension',
        agentSessions[0].extension
      );
      store.dispatch(
        actionCreatorLogin({
          agentCode: agentSessions[0].agent_code,
          extension: agentSessions[0].extension
        })
      );
    } else {
      localStorage.removeItem('currentLoggedInExtension');
      store.dispatch(actionCreatorLogout());
    }
  }, [agentSessions]);

  const handleAddSession = useCallback((newSession) => {
    setAgentSessions((currentSessions) => {
      const loggedAgentCode = localStorage.getItem('agent_code');
      if (loggedAgentCode !== newSession.agent.agent_code)
        return currentSessions;

      const isLoggedIn = !!currentSessions && !!currentSessions.length;

      if (isLoggedIn) {
        const isTheSameExtension =
          currentSessions[0].extension === newSession.extension;

        if (!isTheSameExtension) return currentSessions;
      }

      const changedSessions = cloneDeep(currentSessions);

      const alreadyAdded = changedSessions.find(
        (session) =>
          session.extension === newSession.extension &&
          session.queue.name === newSession.agent.queue.name
      );

      if (alreadyAdded) {
        Object.assign(alreadyAdded, {
          state: newSession.state,
          date: newSession.date,
          is_incoming_call: newSession.isIncomingCall,
          pause_id: newSession.agent.pause_id,
          agent: newSession.agent
        });

        return [...changedSessions];
      }

      const newSessionDTO = {
        state: newSession.state,
        extension: newSession.extension,
        caller: null,
        date: newSession.date,
        is_incoming_call: newSession.isIncomingCall,
        pause_id: newSession.agent.pause_id,
        queue: newSession.agent.queue,
        agent: newSession.agent,
        agent_code: newSession.agent?.agent_code
      };

      return [...currentSessions, newSessionDTO];
    });
  }, []);

  const handleRemoveSession = useCallback((session) => {
    setAgentSessions((prev) => {
      const newItems = prev.filter(
        (item) =>
          item.queue.name !== session.queueName ||
          item.extension !== session.extension
      );

      if (newItems.length === prev.length) return prev;

      return [...newItems];
    });
  }, []);

  const changeToUnplaced = useCallback((eventData) => {
    setAgentSessions((currentSessions) => {
      setActivePauseCallData(null);
      const changedSessions = cloneDeep(currentSessions);

      changedSessions.forEach((session) => {
        if (session.extension === eventData.extension) {
          session.shouldEnablePauseForm = !eventData.agent?.pause_id;
          session.state = queueAgentStates.UNPLACED;
          session.caller = null;
          session.date = eventData.date;
          session.is_incoming_call = null;
          session.pause_id = null;
          session.agent = eventData.agent;
        }
      });

      return changedSessions;
    });
  }, []);

  const changeToPause = useCallback((eventData) => {
    setCurrentPauseId(eventData.agent.pause_id);
    const state =
      eventData.state === 'PAUSE_AUTO'
        ? queueAgentStates.PAUSE_AUTO
        : queueAgentStates.PAUSE;
    setAgentSessions((currentSessions) => {
      setActivePauseCallData(null);
      const changedSessions = cloneDeep(currentSessions);

      changedSessions.forEach((session) => {
        if (session.extension === eventData.extension) {
          session.state = state;
          session.caller = null;
          session.date = eventData.date;
          session.is_incoming_call = null;
          session.agent = eventData.agent;
          session.shouldEnablePauseForm = false;
          session.pause_id = eventData.agent?.pause?.id;
        }
      });

      return changedSessions;
    });
    setIsLoading(false);
  }, []);

  const changeToAvailable = useCallback((eventData) => {
    setAgentSessions((currentSessions) => {
      setActivePauseCallData(null);
      const changedSessions = cloneDeep(currentSessions);

      changedSessions.forEach((session) => {
        if (session.extension === eventData.extension) {
          session.state = queueAgentStates.AVAILABLE;
          session.caller = null;
          session.date = eventData.date;
          session.is_incoming_call = null;
          session.pause_id = null;
          session.shouldEnablePauseForm = true;
          session.agent = eventData.agent;
        }
      });

      return changedSessions;
    });
  }, []);

  const changeToRing = useCallback((eventData) => {
    setAgentSessions((currentSessions) => {
      const isCurrentPaused = currentSessions.some(
        (session) =>
          !!session.pause_id &&
          (session.state === queueAgentStates.PAUSE ||
            session.action === queueAgentStates.PAUSE)
      );

      if (
        isCurrentPaused &&
        currentSessions[0].extension === eventData.extension
      ) {
        setActivePauseCallData(queueAgentStates.RING);
        return currentSessions;
      } else {
        setActivePauseCallData(null);
      }

      const changedSessions = cloneDeep(currentSessions);
      changedSessions.forEach((session) => {
        if (session.extension === eventData.extension) {
          session.shouldEnablePauseForm =
            eventData.agent?.action !== queueAgentStates.PAUSE;
          session.state = queueAgentStates.RING;
          session.caller = null;
          session.date = eventData.date;
          session.is_incoming_call = eventData.isIncomingCall;
          session.pause_id = null;
          session.agent = eventData.agent;
        }
      });

      return changedSessions;
    });
  }, []);

  const changeToRinging = useCallback((eventData) => {
    setAgentSessions((currentSessions) => {
      const isCurrentPaused = currentSessions.some(
        (session) =>
          !!session.pause_id &&
          (session.state === queueAgentStates.PAUSE ||
            session.action === queueAgentStates.PAUSE)
      );

      if (
        isCurrentPaused &&
        currentSessions[0].extension === eventData.extension
      ) {
        setActivePauseCallData(queueAgentStates.RINGING);
        return currentSessions;
      } else {
        setActivePauseCallData(null);
      }

      const changedSessions = cloneDeep(currentSessions);
      changedSessions.forEach((session) => {
        if (session.extension === eventData.extension) {
          session.shouldEnablePauseForm =
            eventData.agent?.action !== queueAgentStates.PAUSE;
          session.state = queueAgentStates.RINGING;
          session.caller = null;
          session.date = eventData.date;
          session.is_incoming_call = true;
          session.pause_id = null;
          session.agent = eventData.agent;
        }
      });

      return changedSessions;
    });
  }, []);

  const changeToTabulationPause = useCallback((eventData) => {
    setAgentSessions((currentSessions) => {
      const changedSessions = cloneDeep(currentSessions);
      changedSessions.forEach((session) => {
        if (session.extension === eventData.extension) {
          session.state = queueAgentStates.TABULATION_PAUSE;
          session.caller = null;
          session.date = eventData.date;
          session.is_incoming_call = null;
          session.pause_id = null;
          session.agent = eventData.agent;
        }
      });

      return changedSessions;
    });
  }, []);

  const getAgentStatus = useCallback(() => {
    const agentCode = localStorage.getItem('agent_code');
    setIsLoading(true);
    apiUc
      .get(`/api/agent-status/${agentCode}`)
      .then((response) => {
        const sessionData = response?.data?.data ?? [];

        const sessionQueueIds = sessionData.map((session) => session.queue_id);

        const loggedPhoneQueues = queues.filter((q) =>
          sessionQueueIds.includes(q.id)
        );

        /** dispara um update para o redux, atualizando as filas logadas */
        dispatch(updateLoggedPhoneQueues(loggedPhoneQueues));

        setAgentSessions(sessionData);
      })
      .catch(() => {
        setAgentSessions([]);
        dispatch(updateLoggedPhoneQueues([]));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [queues]);

  const alertWarning = useCallback((eventData) => {
    const agentLocal = localStorage.getItem('agent_code');
    const agentSocket = eventData.agentCode;
    const notificationMessage =
      'Você irá entrar em pausa automática em 10 minutos!';

    if (agentSocket === agentLocal) {
      toast.info(notificationMessage);

      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Aviso de Pausa Automática', {
          body: notificationMessage
        });
      }
    }
  }, []);

  useEffect(() => {
    PauseService.getAllPauses()
      .then((response) => {
        setAllPauses(response.data.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    socket.on('connect', () => {
      console.log('Telephony service panel socket connected!');
    });

    return () => {
      if (socket) socket.disconnect();
    };
  }, [socket]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_ADDED, (eventData) => {
      handleAddSession(eventData);
    });
  }, [socket, handleAddSession]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_REMOVED, async (eventData) => {
      const currentLoggedInExtension = localStorage.getItem(
        'currentLoggedInExtension'
      );
      const agentCode = localStorage.getItem('agent_code');
      const isUsingScale = localStorage.getItem('is_using_scale');
      if (
        eventData.extension === currentLoggedInExtension &&
        JSON.parse(isUsingScale)
      ) {
        try {
          await apiUc.get(`/api/agent-status/${agentCode}`);
        } catch {
          window.location.href = '/activelogout';
        }
      }
      return handleRemoveSession(eventData);
    });
  }, [socket, handleRemoveSession]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_REMOVED, (eventData) => {
      return handleRemoveSession(eventData);
    });
  }, [socket, handleRemoveSession]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_UNREGISTERED, (eventData) => {
      changeToUnplaced(eventData);
    });
  }, [socket, changeToUnplaced]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_PAUSE, (eventData) => {
      const localLogin = localStorage.getItem('login');
      if (eventData.agent?.usr?.login === localLogin) {
        setIsLoading(true);
        changeToPause(eventData);
      }
    });
  }, [socket, changeToPause]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_PAUSE_ALERT, (eventData) => {
      const localLogin = localStorage.getItem('login');
      if (eventData.agent?.usr?.login === localLogin) {
        changeToPause(eventData);
      }
    });
  }, [socket, changeToPause]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_TABULATION_PAUSE, (eventData) => {
      changeToTabulationPause(eventData);
    });
  }, [socket, changeToTabulationPause]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_AVAILABLE, (eventData) => {
      changeToAvailable(eventData);
    });
  }, [socket, changeToAvailable]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_RING, (eventData) => {
      changeToRing(eventData);
    });
  }, [socket, changeToRing]);

  useEffect(() => {
    socket.on(queueAgentEvents.QUEUE_MEMBER_RINGING, (eventData) => {
      changeToRinging(eventData);
    });
  }, [socket, changeToRinging]);
  useEffect(() => {
    socketWarning.on(queueAgentEvents.AUTOMATIC_PAUSE_ALERT, (eventData) => {
      alertWarning(eventData);
    });
  }, [socketWarning, alertWarning]);

  useEffect(() => {
    getAgentStatus();
  }, [getAgentStatus]);

  useEffect(() => {
    apiUc
      .get('/api/ramais/all')
      .then(({ data }) => {
        const allowedExtension = [];
        const agentExtensions = JSON.parse(localStorage.getItem('extensions'));

        data.data.forEach((extension) => {
          agentExtensions.forEach((agentExtension) => {
            if (extension.extension_number === agentExtension.ramal) {
              allowedExtension.push(extension);
            }
          });
        });

        setExtensions([...allowedExtension]);
      })
      .catch(() => {
        toast.error('Não foi possível carregar os ramais', {
          autoClose: 3000,
          closeOnClick: true
        });
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!agentSessions) return;
    const internalAllPauses = agentSessions
      .map((element) => element.queue.pauses)
      .reduce((prevPauses, currentPauses = []) => {
        const prevIds = prevPauses.map((p) => p.id);
        const validCurrentPauses = currentPauses.filter(
          (p) => !prevIds.includes(p.id)
        );

        return [...prevPauses, ...validCurrentPauses];
      }, [])
      .filter((p) => !p?.disable);

    setPauses(internalAllPauses);

    const isAgentPaused = agentSessions.some(
      (session) =>
        !!session.pause_id &&
        (session.state === queueAgentStates.PAUSE ||
          session.state === queueAgentStates.PAUSE_AUTO ||
          session.action === queueAgentStates.PAUSE)
    );

    const isOnActivePauseCall = agentSessions.some(
      (session) =>
        !!session.pause_id &&
        (session.action === queueAgentStates.RING ||
          session.action === queueAgentStates.RINGING)
    );

    setIsPaused(isAgentPaused || isOnActivePauseCall);

    if (isAgentPaused || isOnActivePauseCall) {
      setCurrentPauseId(agentSessions[0].pause_id);
    }
  }, [agentSessions]);

  const showError = (message) => {
    toast.error(message, {
      autoClose: 3000,
      closeOnClick: true
    });
  };

  const handleLogin = (data) => {
    const agent_code = localStorage.getItem('agent_code');
    setIsLoading(true);
    apiUc
      .post('/api/agent-session', {
        ...data,
        agent_code
      })
      .catch((error) => {
        const msg =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message;
        showError(msg || 'Ocorreu um erro ao tentar logar na fila');
      })
      .finally(() => {
        setIsLoading(false);
        getAgentStatus();
      });
  };

  const handleAddQueues = useCallback(({ queueIds, extension }) => {
    const agentCode = localStorage.getItem('agent_code');

    setIsLoading(true);
    setAddAnotherQueue(false);

    apiUc
      .post('/api/agent-session', {
        queueIds,
        agent_code: agentCode,
        extension: extension
          ? extension
          : localStorage.getItem('currentLoggedInExtension')
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message || 'Erro ao fazer login';
        showError(errorMessage);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handlePause = useCallback(({ pauseId }) => {
    const agentCode = localStorage.getItem('agent_code');
    setIsLoading(true);

    const pause_id = Number(pauseId);

    apiUc
      .post(`/api/agent-pause/${agentCode}`, { pause_id })
      .then(() => {
        setIsPaused(true);
        setCurrentPauseId(pause_id);
      })
      .catch(() => {
        showError('Não foi possível pausar agente');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleUnpause = useCallback(() => {
    const agentCode = localStorage.getItem('agent_code');
    setIsLoading(true);
    apiUc
      .delete(`/api/agent-pause/${agentCode}`)
      .then(() => {
        setIsPaused(false);
        setCurrentPauseId('');
      })
      .catch((err) => {
        showError(err.response.data.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleLogoff = ({ queuesToRemove = [] }) => {
    const agentCode = localStorage.getItem('agent_code');
    const extension = agentSessions[0].extension;

    if (!agentCode) return showError('Código de Agente não encontrado');

    const queueIds = queuesToRemove.map((q) => q.id);

    setIsLoading(true);
    apiUc({
      method: 'delete',
      data: {
        extension,
        queueIds,
        agent_code: agentCode
      },
      url: '/api/agent-session'
    })
      .then(() => {
        queuesToRemove.forEach((q) => {
          handleRemoveSession({
            queueName: q.name,
            extension
          });
        });
      })
      .catch(({ response, message }) => {
        const { data } = response;
        showError((data && data.message) || message);
      })
      .finally(() => (setIsLoading(false), getAgentStatus()));
  };

  const handleHideAddQueueForm = () => {
    setAddAnotherQueue(false);
  };

  let componentToRender = (
    <LoginForm
      extensions={extensions}
      queues={queues}
      onSubmitHandler={handleLogin}
      setIsDropdownOpen={setIsDropdownOpen}
    />
  );

  if (isLogged) {
    componentToRender = (
      <>
        <HeadersModalLoginQueues
          isChat={false}
          extensionName={agentSessions[0]?.extension || ''}
        />

        <PauseUnpauseForm
          pauses={pauses}
          allPauses={allPauses}
          isPaused={isPaused}
          currentPauseId={currentPauseId}
          handlePause={handlePause}
          handleUnpause={handleUnpause}
        />

        <LogoutForm
          agentQueues={agentSessions}
          isPaused={isPaused}
          handleLogoff={handleLogoff}
        />

        {agentSessions.map((agentSession, index) => (
          <ServicePanelItem
            key={index}
            agentSession={agentSession}
            handleLogoff={handleLogoff}
            isPaused={isPaused}
          />
        ))}

        <ButtonLoginInNewQueue action={() => setAddAnotherQueue(true)} />
      </>
    );
  }

  if (isLoading) {
    componentToRender = (
      <S.LoadingWrapper>
        <ReactLoader height={50} width={50} />
      </S.LoadingWrapper>
    );
  }

  const queuesForLogin = [];

  const loggedPhoneQueues = agentSessions.map((s) => s.queue.id);

  queues.map((q) => {
    if (!loggedPhoneQueues.includes(q.id)) {
      queuesForLogin.push(q);
    }
  });

  if (addAnotherQueue) {
    componentToRender = (
      <AddQueueForm
        handlerAddQueue={handleAddQueues}
        handleCancel={handleHideAddQueueForm}
        queues={queuesForLogin}
        agentSessions={agentSessions}
      />
    );
  }

  const getIconColorByStatus = useCallback(() => {
    const defaultColor = 'transparent';
    if (!isLogged) return defaultColor;
    return (
      agentStatusColors[
        activePauseCallData || agentSessions[0].state || agentSessions[0].action
      ] || defaultColor
    );
  }, [agentSessions, isLogged, activePauseCallData]);

  const toggleDropdown = () => setIsDropdownOpen((prevState) => !prevState);

  return (
    <Dropdown nav isOpen={isDropdownOpen} toggle={toggleDropdown}>
      <DropdownToggle
        className="nav-link"
        tag="a"
        style={{ cursor: 'pointer', marginLeft: '1rem', marginRight: '0.6rem' }}
      >
        <FiPhone
          strokeWidth={isLogged ? 1.2 : 2}
          style={{
            cursor: 'pointer',
            color: 'white',
            borderRadius: '5%',
            fontWeight: 'bold',
            fontSize: '1.6rem',
            fill: getIconColorByStatus()
          }}
        />
        {isLogged && <UptimeCounter startDate={agentSessions[0]?.date} />}
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-xl py-0 overflow-y-visible" right>
        {componentToRender}
      </DropdownMenu>
    </Dropdown>
  );
};
